/**
 * Hides individual Reviews.io reviews below a minimum star rating.
 *
 * This is the second half of the review threshold. Products rated below the
 * threshold have their whole widget hidden server side by
 * snippets/reviews-visibility.liquid; this handles the low reviews that remain
 * on products that clear it.
 *
 * The Reviews.io "Polaris" widget is an app block with no minimum-rating
 * setting, so filtering has to happen after its Vue app renders reviews into
 * the DOM. Each review is a `.R-ContentList__item` containing a
 * `.R-RatingStars__stars` element whose aria-label starts with the rating.
 *
 * Q&A entries share the `.R-ContentList__item` class but have no star rating,
 * so items without a readable rating are left untouched.
 */

// assets/reviewsio-hide-low-ratings.js
(() => {
  const WIDGET_SELECTOR = 'reviewsio-product-reviews-widget';
  const ITEM_SELECTOR = '.R-ContentList__item';
  const STARS_SELECTOR = '.R-RatingStars__stars';

  const script =
    document.currentScript ||
    document.querySelector('script[data-reviews-min-rating]');
  const minRating = parseFloat(script?.dataset.reviewsMinRating);

  if (!Number.isFinite(minRating) || minRating <= 0) return;

  const getRating = (item) => {
    const stars = item.querySelector(STARS_SELECTOR);

    // Guard against reading the rating of a nested item.
    if (!stars || stars.closest(ITEM_SELECTOR) !== item) return null;

    const label =
      stars.getAttribute('aria-label') || stars.getAttribute('title') || '';
    const rating = parseFloat(label);

    return Number.isFinite(rating) ? rating : null;
  };

  const hideItem = (item) => {
    if (item.dataset.lowRatingHidden === 'true') return;

    item.dataset.lowRatingHidden = 'true';
    item.setAttribute('hidden', '');
    item.style.setProperty('display', 'none', 'important');
  };

  // Vue reuses DOM nodes between pages of results, so a node hidden for a
  // 1 star review can later hold a 5 star one. Only reset what we hid.
  const showItem = (item) => {
    if (item.dataset.lowRatingHidden !== 'true') return;

    delete item.dataset.lowRatingHidden;
    item.removeAttribute('hidden');
    item.style.removeProperty('display');
  };

  const filterWidget = (widget) => {
    widget.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      const rating = getRating(item);
      if (rating === null) return;

      if (rating < minRating) {
        hideItem(item);
      } else {
        showItem(item);
      }
    });
  };

  const watchWidget = (widget) => {
    // Only childList mutations are observed, so the attribute and style
    // changes made while filtering cannot retrigger this observer.
    const observer = new MutationObserver(() => filterWidget(widget));

    filterWidget(widget);
    observer.observe(widget, { childList: true, subtree: true });
  };

  const init = () => {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(watchWidget);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
